import { CalendarDate } from "@/features/ui/Calendar";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useRef, useState } from "react";

export const useDeliveryDateInputs = (initialLocalDate: string | null = "") => {
  const [inputValues, setInputValues] = useState<{ [key: string]: string }>({});
  const [selectedDates, setSelectedDates] = useState<{ [key: string]: CalendarDate }>({});
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const focusedInputRef = useRef<string | null>(null);

  // 단일 입력 변경 (개별 row)
  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>, rowId: string) => {
    const value = event.target.value.replace(/[^0-9]/g, ""); // 숫자만
    setInputValues((prev) => ({ ...prev, [rowId]: value }));
    focusedInputRef.current = rowId;
  }, []);

  // 다중 입력 변경 (상단 입력 → 전체 row 반영)
  const handleAllInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, rowIds: string[], fallbackDate?: string) => {
      const value = event.target.value.replace(/[^0-9]/g, "");
      const dateValue = value || fallbackDate || "";

      setInputValues((prev) => {
        const updated = { ...prev };
        rowIds.forEach((rowId) => {
          updated[rowId] = dateValue;
        });
        return updated;
      });
    },
    [],
  );

  // 단일 날짜 선택 변경
  const handleDateChange = useCallback((date: CalendarDate, rowId: string) => {
    const formatted = dayjs(date[0]).format("YYYYMMDD");

    setInputValues((prev) => ({ ...prev, [rowId]: formatted }));
    setSelectedDates((prev) => ({
      ...prev,
      [rowId]: [date[0], prev[rowId]?.[1] || new Date()],
    }));

    inputRefs.current[rowId]?.focus?.();
    focusedInputRef.current = rowId;
  }, []);

  // 다중 날짜 선택 변경 (상단 달력 → 전체 row 반영)
  const handleAllDateChange = useCallback((date: CalendarDate, rowIds: string[]) => {
    const formatted = dayjs(date[0]).format("YYYYMMDD");

    setInputValues((prev) => {
      const updated = { ...prev };
      rowIds.forEach((rowId) => {
        updated[rowId] = formatted;
      });
      return updated;
    });

    setSelectedDates((prev) => {
      const updated = { ...prev };
      rowIds.forEach((rowId) => {
        updated[rowId] = [date[0], prev[rowId]?.[1] || new Date()];
      });
      return updated;
    });
  }, []);

  return {
    inputValues,
    setInputValues,
    selectedDates,
    inputRefs,
    focusedInputRef,
    handleInputChange,
    handleAllInputChange,
    handleDateChange,
    handleAllDateChange,
  };
};
